import React from "react";
import { Reveal } from "./shared/Reveal";

const NERVE_NODES = [
  "Buildings & Sites",
  "HVAC & Refrigeration",
  "Solar PV & Battery",
  "EV Charging",
  "Circuits & Assets",
  "Water & Gas",
  "Occupancy",
  "Carbon & ESG",
];

// Circular "nervous system" schematic: every data source arranged in a ring,
// feeding pulses into the central CII hub.
export default function CIIOrbitDiagram() {
  const W = 900, H = 560, cx = 450, cy = 280, rx = 340, ry = 200, hubR = 60;
  const nodes = NERVE_NODES.map((label, i) => {
    const a = (Math.PI * 2 * i) / NERVE_NODES.length - Math.PI / 2;
    const x = cx + rx * Math.cos(a);
    const y = cy + ry * Math.sin(a);
    const hx = cx + hubR * Math.cos(a);
    const hy = cy + hubR * Math.sin(a);
    const above = y < cy - 8;
    return { label, x, y, hx, hy, labelY: above ? y - 20 : y + 30 };
  });
  return (
    <Reveal className="nervewrap" tag="div" data-testid="cii-orbit-diagram">
      <svg viewBox={`0 0 ${W} ${H}`} className="nervesvg" role="img" aria-label="Central Intelligence Infrastructure nervous system diagram — every data source feeding the central CII hub">
        <defs>
          <radialGradient id="hubGrad" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#F0C46A" />
            <stop offset="55%" stopColor="#E5A13B" />
            <stop offset="100%" stopColor="#B9781F" />
          </radialGradient>
          <filter id="hubGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="10" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* spokes */}
        {nodes.map((n, i) => (
          <path key={`p${i}`} d={`M ${n.x} ${n.y} L ${n.hx} ${n.hy}`}
            fill="none" stroke="rgba(34,169,224,.42)" strokeWidth="1.6" strokeDasharray="4 6" />
        ))}
        {/* data pulses flowing into the hub */}
        {nodes.map((n, i) => (
          <circle key={`pulse${i}`} r="4.2" fill="#F0C46A">
            <animateMotion dur="2.6s" repeatCount="indefinite" begin={`${(i * 0.32).toFixed(2)}s`}
              path={`M ${n.x} ${n.y} L ${n.hx} ${n.hy}`} />
            <animate attributeName="opacity" values="0;1;1;0" dur="2.6s" repeatCount="indefinite" begin={`${(i * 0.32).toFixed(2)}s`} />
          </circle>
        ))}

        {/* outer source nodes */}
        {nodes.map((n, i) => (
          <g key={`n${i}`}>
            <circle cx={n.x} cy={n.y} r="9" fill="#0B1533" stroke="#22A9E0" strokeWidth="2" />
            <circle cx={n.x} cy={n.y} r="3.4" fill="#22A9E0">
              <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" repeatCount="indefinite" begin={`${(i * 0.3).toFixed(2)}s`} />
            </circle>
            <text x={n.x} y={n.labelY} textAnchor="middle" className="nervelabel">{n.label}</text>
          </g>
        ))}

        {/* central CII hub */}
        <circle cx={cx} cy={cy} r={hubR + 16} fill="none" stroke="rgba(240,196,106,.28)" strokeWidth="1.4">
          <animate attributeName="r" values={`${hubR + 10};${hubR + 22};${hubR + 10}`} dur="3.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0.1;0.5" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={hubR} fill="url(#hubGrad)" filter="url(#hubGlow)" />
        <text x={cx} y={cy + 6} textAnchor="middle" className="hubtitle">CII&#8482;</text>
      </svg>
      <div className="nervecaption">The intelligence infrastructure supporting one connected, centrally coordinated environment.</div>
    </Reveal>
  );
}
